import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

/* ── config ── */
const LIMIT = 20;

const PAY_STATUS = {
  paid: {
    label: "Paid",
    color: "#16a34a",
    bg: "rgba(22,163,74,0.1)",
    border: "rgba(22,163,74,0.25)",
    icon: "✅",
  },
  pending: {
    label: "Pending",
    color: "#d97706",
    bg: "rgba(245,158,11,0.1)",
    border: "rgba(245,158,11,0.25)",
    icon: "🕐",
  },
  failed: {
    label: "Failed",
    color: "#dc2626",
    bg: "rgba(239,68,68,0.1)",
    border: "rgba(239,68,68,0.25)",
    icon: "✕",
  },
  refunded: {
    label: "Refunded",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.1)",
    border: "rgba(124,58,237,0.25)",
    icon: "↩",
  },
};

const PAY_METHOD = {
  razorpay: { label: "Razorpay", icon: "💳", color: "#2563eb" },
  cod: { label: "COD", icon: "💵", color: "#16a34a" },
  upi: { label: "UPI", icon: "📱", color: "#7c3aed" },
  online: { label: "Online", icon: "🌐", color: "#0891b2" },
};
const getMethod = (m) =>
  PAY_METHOD[m] || { label: m || "—", icon: "💳", color: "#6b7280" };

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/* ── sub-components ── */
const StatusChip = ({ status }) => {
  const s = PAY_STATUS[status] || PAY_STATUS.pending;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 100,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {s.icon} {s.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, sub, color }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 16,
      padding: "18px 20px",
      border: "1px solid #f0f0f0",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <span style={{ fontSize: 22 }}>{icon}</span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 800,
          padding: "2px 8px",
          borderRadius: 100,
          background: `${color}15`,
          color,
        }}
      >
        {sub}
      </span>
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 900,
        color: "#111827",
        letterSpacing: "-0.5px",
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 11,
        color: "#9ca3af",
        fontWeight: 600,
        marginTop: 3,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {label}
    </div>
  </div>
);

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const debounceRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get("/admin/payments/summary");
      setSummary(res.data);
    } catch {}
  }, []);

  const fetchPayments = useCallback(
    async (pg = page) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: pg, limit: LIMIT });
        if (statusFilter !== "all")
          params.append("paymentStatus", statusFilter);
        if (methodFilter !== "all")
          params.append("paymentMethod", methodFilter);
        if (search.trim()) params.append("search", search.trim());
        const res = await api.get(`/admin/payments?${params}`);
        setPayments(res.data.payments || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      } catch {
        toast.error("Failed to load payments");
      } finally {
        setLoading(false);
      }
    },
    [page, statusFilter, methodFilter, search],
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  useEffect(() => {
    fetchPayments(page);
  }, [page, statusFilter, methodFilter]); // eslint-disable-line
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPayments(1);
    }, 400);
  }, [search]); // eslint-disable-line

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await api.patch(`/admin/payments/${orderId}/status`, {
        paymentStatus: newStatus,
      });
      toast.success(`Payment marked as ${newStatus}`);
      fetchPayments(page);
      fetchSummary();
      if (detailOrder?._id === orderId)
        setDetailOrder((p) => ({ ...p, paymentStatus: newStatus }));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } finally {
      setUpdatingId(null);
    }
  };

  /* pagination pages list with ellipsis */
  const pageList = () => {
    const out = [];
    for (let p = 1; p <= pages; p++) {
      if (p === 1 || p === pages || (p >= page - 2 && p <= page + 2))
        out.push(p);
    }
    const withGaps = [];
    out.forEach((p, i) => {
      if (i > 0 && p - out[i - 1] > 1) withGaps.push("…");
      withGaps.push(p);
    });
    return withGaps;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <AdminNavbar />

      {/* ── hero ── */}
      <div className="page-gradient py-6">
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h1
                style={{
                  color: "#fff",
                  fontSize: 26,
                  fontWeight: 900,
                  margin: "0 0 3px",
                }}
              >
                💳 Payments
              </h1>
              <p
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 13,
                  margin: 0,
                }}
              >
                Revenue overview · Transaction history · Payment management
              </p>
            </div>
            <button
              onClick={() => {
                fetchPayments(page);
                fetchSummary();
              }}
              style={{
                padding: "9px 20px",
                borderRadius: 100,
                border: "none",
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "24px 20px 60px",
          boxSizing: "border-box",
        }}
      >
        {/* ── summary cards ── */}
        {summary && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 14,
                marginBottom: 16,
              }}
            >
              <StatCard
                icon="💰"
                label="Total Revenue"
                value={fmt(summary.totalRevenue)}
                sub={`${summary.paidCount} paid`}
                color="#16a34a"
              />
              <StatCard
                icon="🕐"
                label="Pending"
                value={fmt(summary.pendingAmount)}
                sub={`${summary.pendingCount} orders`}
                color="#d97706"
              />
              <StatCard
                icon="✕"
                label="Failed"
                value={fmt(summary.failedAmount)}
                sub={`${summary.failedCount} orders`}
                color="#dc2626"
              />
              <StatCard
                icon="↩"
                label="Refunded"
                value={fmt(summary.refundAmount)}
                sub="total"
                color="#7c3aed"
              />
            </div>

            {/* method breakdown */}
            {summary.byMethod?.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: "16px 20px",
                  border: "1px solid #f0f0f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  marginBottom: 20,
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    margin: "0 0 12px",
                  }}
                >
                  Revenue by Payment Method
                </p>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  {summary.byMethod.map((m) => {
                    const mt = getMethod(m._id);
                    const pct = summary.totalRevenue
                      ? Math.round((m.total / summary.totalRevenue) * 100)
                      : 0;
                    return (
                      <div
                        key={m._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 18 }}>{mt.icon}</span>
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: "#111827",
                              }}
                            >
                              {fmt(m.total)}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                padding: "1px 7px",
                                borderRadius: 100,
                                background: `${mt.color}15`,
                                color: mt.color,
                              }}
                            >
                              {pct}%
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>
                            {mt.label} · {m.count} orders
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── filters ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #f0f0f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 20px",
              borderBottom: "1px solid #f5f5f5",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {/* search */}
            <div style={{ position: "relative", flex: "1 1 220px" }}>
              <span
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 13,
                }}
              >
                🔍
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order ID, customer, Razorpay ID…"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "8px 12px 8px 32px",
                  borderRadius: 10,
                  border: "1.5px solid #e5e7eb",
                  fontSize: 13,
                  outline: "none",
                  background: "#f9fafb",
                  color: "#111827",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2D8B4E";
                  e.target.style.background = "#fff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.background = "#f9fafb";
                }}
              />
            </div>

            {/* status filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1.5px solid #e5e7eb",
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option value="all">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>

            {/* method filter */}
            <select
              value={methodFilter}
              onChange={(e) => {
                setMethodFilter(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1.5px solid #e5e7eb",
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option value="all">All Methods</option>
              <option value="razorpay">Razorpay</option>
              <option value="cod">COD</option>
              <option value="upi">UPI</option>
            </select>

            <span
              style={{
                fontSize: 12,
                color: "#9ca3af",
                marginLeft: "auto",
                whiteSpace: "nowrap",
              }}
            >
              {total} record{total !== 1 ? "s" : ""}
            </span>
          </div>

          {/* ── table ── */}
          {loading ? (
            <div style={{ padding: "60px 0", textAlign: "center" }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#2D8B4E",
                  animation: "spin 0.7s linear infinite",
                  margin: "0 auto 10px",
                }}
              />
              <p style={{ fontSize: 13, color: "#9ca3af" }}>
                Loading payments…
              </p>
            </div>
          ) : payments.length === 0 ? (
            <div style={{ padding: "60px 0", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>💳</div>
              <p style={{ fontWeight: 700, color: "#374151" }}>
                No payments found
              </p>
              {(search || statusFilter !== "all" || methodFilter !== "all") && (
                <button
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                    setMethodFilter("all");
                  }}
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    color: "#2D8B4E",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* header row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.4fr 0.8fr 0.9fr 1fr 0.9fr 1fr",
                  gap: 0,
                  padding: "10px 20px",
                  background: "#fafafa",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                {[
                  "Order ID",
                  "Customer",
                  "Amount",
                  "Method",
                  "Payment",
                  "Date",
                  "Actions",
                ].map((h) => (
                  <span
                    key={h}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {/* rows */}
              {payments.map((p, idx) => {
                const mt = getMethod(p.paymentMethod);
                const ps = PAY_STATUS[p.paymentStatus] || PAY_STATUS.pending;
                const isRzp = p.paymentMethod === "razorpay";
                return (
                  <div
                    key={p._id}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "1.2fr 1.4fr 0.8fr 0.9fr 1fr 0.9fr 1fr",
                      gap: 0,
                      padding: "12px 20px",
                      borderBottom:
                        idx < payments.length - 1
                          ? "1px solid #f9fafb"
                          : "none",
                      alignItems: "center",
                      transition: "background 0.12s",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f9fafb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                    onClick={() => setDetailOrder(p)}
                  >
                    {/* order id */}
                    <div>
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: "#2D8B4E",
                        }}
                      >
                        #{p.orderId || "—"}
                      </div>
                      {isRzp && p.razorpayPaymentId && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "#9ca3af",
                            marginTop: 1,
                            fontFamily: "monospace",
                          }}
                        >
                          {p.razorpayPaymentId.slice(0, 16)}…
                        </div>
                      )}
                    </div>

                    {/* customer */}
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#111827",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.customerDetails?.name || "—"}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {p.customerDetails?.phone || ""}
                      </div>
                    </div>

                    {/* amount */}
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 900,
                          color: "#111827",
                        }}
                      >
                        {fmt(p.total)}
                      </div>
                      {p.deliveryFee > 0 && (
                        <div style={{ fontSize: 10, color: "#9ca3af" }}>
                          +{fmt(p.deliveryFee)} del.
                        </div>
                      )}
                    </div>

                    {/* method */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <span style={{ fontSize: 15 }}>{mt.icon}</span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: mt.color,
                        }}
                      >
                        {mt.label}
                      </span>
                    </div>

                    {/* payment status */}
                    <div>
                      <StatusChip status={p.paymentStatus} />
                    </div>

                    {/* date */}
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {fmtDate(p.createdAt)}
                    </div>

                    {/* actions */}
                    <div
                      style={{ display: "flex", gap: 5, flexWrap: "wrap" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.paymentStatus === "pending" && (
                        <button
                          disabled={updatingId === p._id}
                          onClick={() => handleStatusUpdate(p._id, "paid")}
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "4px 10px",
                            borderRadius: 8,
                            border: "none",
                            background: "#16a34a",
                            color: "#fff",
                            cursor: "pointer",
                            opacity: updatingId === p._id ? 0.6 : 1,
                          }}
                        >
                          Mark Paid
                        </button>
                      )}
                      {p.paymentStatus === "paid" && (
                        <button
                          disabled={updatingId === p._id}
                          onClick={() => handleStatusUpdate(p._id, "refunded")}
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "4px 10px",
                            borderRadius: 8,
                            border: "1px solid #ddd6fe",
                            background: "#fff",
                            color: "#7c3aed",
                            cursor: "pointer",
                            opacity: updatingId === p._id ? 0.6 : 1,
                          }}
                        >
                          Refund
                        </button>
                      )}
                      {p.paymentStatus === "failed" && (
                        <button
                          disabled={updatingId === p._id}
                          onClick={() => handleStatusUpdate(p._id, "pending")}
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "4px 10px",
                            borderRadius: 8,
                            border: "1px solid #fde68a",
                            background: "#fff",
                            color: "#d97706",
                            cursor: "pointer",
                            opacity: updatingId === p._id ? 0.6 : 1,
                          }}
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* pagination */}
              {pages > 1 && (
                <div
                  style={{
                    padding: "14px 20px",
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    Showing{" "}
                    <strong style={{ color: "#374151" }}>
                      {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)}
                    </strong>{" "}
                    of <strong style={{ color: "#374151" }}>{total}</strong>
                  </span>
                  <div
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        height: 30,
                        padding: "0 12px",
                        borderRadius: 8,
                        border: "1.5px solid #e5e7eb",
                        background: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        color: page === 1 ? "#d1d5db" : "#374151",
                        cursor: page === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      ← Prev
                    </button>
                    {pageList().map((p, i) =>
                      p === "…" ? (
                        <span
                          key={`g${i}`}
                          style={{
                            width: 30,
                            textAlign: "center",
                            fontSize: 13,
                            color: "#9ca3af",
                          }}
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            border: "1.5px solid",
                            borderColor: p === page ? "#2D8B4E" : "#e5e7eb",
                            background: p === page ? "#2D8B4E" : "#fff",
                            color: p === page ? "#fff" : "#374151",
                            fontSize: 12,
                            fontWeight: p === page ? 800 : 500,
                            cursor: "pointer",
                          }}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() => setPage((p) => Math.min(pages, p + 1))}
                      disabled={page === pages}
                      style={{
                        height: 30,
                        padding: "0 12px",
                        borderRadius: 8,
                        border: "1.5px solid #e5e7eb",
                        background: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        color: page === pages ? "#d1d5db" : "#374151",
                        cursor: page === pages ? "not-allowed" : "pointer",
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Payment detail modal ── */}
      {detailOrder &&
        (() => {
          const mt = getMethod(detailOrder.paymentMethod);
          const ps =
            PAY_STATUS[detailOrder.paymentStatus] || PAY_STATUS.pending;
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(4px)",
                }}
                onClick={() => setDetailOrder(null)}
              />
              <div
                style={{
                  position: "relative",
                  background: "#fff",
                  borderRadius: 20,
                  width: "100%",
                  maxWidth: 520,
                  maxHeight: "90vh",
                  overflowY: "auto",
                  boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
                }}
              >
                {/* modal header */}
                <div
                  style={{
                    padding: "18px 22px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "sticky",
                    top: 0,
                    background: "#fff",
                    borderRadius: "20px 20px 0 0",
                    zIndex: 2,
                  }}
                >
                  <div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <h2
                        style={{
                          fontSize: 16,
                          fontWeight: 900,
                          color: "#111827",
                          margin: 0,
                        }}
                      >
                        #{detailOrder.orderId}
                      </h2>
                      <StatusChip status={detailOrder.paymentStatus} />
                    </div>
                    <p
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        margin: "2px 0 0",
                      }}
                    >
                      {fmtDate(detailOrder.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => setDetailOrder(null)}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      border: "none",
                      background: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "pointer",
                      fontSize: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div
                  style={{
                    padding: "20px 22px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {/* amount + method */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: "14px 16px",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: ".6px",
                          margin: "0 0 6px",
                        }}
                      >
                        Amount
                      </p>
                      <p
                        style={{
                          fontSize: 24,
                          fontWeight: 900,
                          color: "#15803d",
                          margin: 0,
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {fmt(detailOrder.total)}
                      </p>
                      {detailOrder.deliveryFee > 0 && (
                        <p
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            margin: "3px 0 0",
                          }}
                        >
                          incl. {fmt(detailOrder.deliveryFee)} delivery
                        </p>
                      )}
                    </div>
                    <div
                      style={{
                        background: "#f9fafb",
                        borderRadius: 12,
                        padding: "14px 16px",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: ".6px",
                          margin: "0 0 8px",
                        }}
                      >
                        Method
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 22 }}>{mt.icon}</span>
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 800,
                            color: mt.color,
                          }}
                        >
                          {mt.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* customer */}
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 12,
                      padding: "14px 16px",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: ".6px",
                        margin: "0 0 8px",
                      }}
                    >
                      Customer
                    </p>
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#111827",
                        margin: "0 0 2px",
                      }}
                    >
                      {detailOrder.customerDetails?.name || "—"}
                    </p>
                    <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                      {detailOrder.customerDetails?.phone}
                    </p>
                    {detailOrder.customerDetails?.email && (
                      <p
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          margin: "2px 0 0",
                        }}
                      >
                        {detailOrder.customerDetails.email}
                      </p>
                    )}
                  </div>

                  {/* Razorpay IDs — shown only for Razorpay payments */}
                  {detailOrder.paymentMethod === "razorpay" && (
                    <div
                      style={{
                        background: "rgba(37,99,235,0.04)",
                        borderRadius: 12,
                        padding: "14px 16px",
                        border: "1px solid rgba(37,99,235,0.15)",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#2563eb",
                          textTransform: "uppercase",
                          letterSpacing: ".6px",
                          margin: "0 0 10px",
                        }}
                      >
                        💳 Razorpay Details
                      </p>
                      {[
                        {
                          label: "Payment ID",
                          value: detailOrder.razorpayPaymentId,
                        },
                        {
                          label: "Order ID",
                          value: detailOrder.razorpayOrderId,
                        },
                      ].map((row) => (
                        <div
                          key={row.label}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <span style={{ fontSize: 11, color: "#6b7280" }}>
                            {row.label}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#111827",
                              fontFamily: "monospace",
                              background: "#f0f7ff",
                              padding: "2px 8px",
                              borderRadius: 6,
                            }}
                          >
                            {row.value || "N/A"}
                          </span>
                        </div>
                      ))}
                      {detailOrder.razorpayPaymentId && (
                        <a
                          href={`https://dashboard.razorpay.com/app/payments/${detailOrder.razorpayPaymentId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#2563eb",
                            textDecoration: "none",
                            marginTop: 4,
                          }}
                        >
                          View on Razorpay Dashboard →
                        </a>
                      )}
                    </div>
                  )}

                  {/* actions */}
                  {detailOrder.paymentStatus !== "refunded" && (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {detailOrder.paymentStatus === "pending" && (
                        <button
                          disabled={updatingId === detailOrder._id}
                          onClick={() =>
                            handleStatusUpdate(detailOrder._id, "paid")
                          }
                          style={{
                            flex: 1,
                            padding: "10px",
                            borderRadius: 10,
                            border: "none",
                            background:
                              "linear-gradient(135deg,#15803d,#22c55e)",
                            color: "#fff",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                            opacity: updatingId === detailOrder._id ? 0.6 : 1,
                          }}
                        >
                          ✅ Mark as Paid
                        </button>
                      )}
                      {detailOrder.paymentStatus === "paid" && (
                        <button
                          disabled={updatingId === detailOrder._id}
                          onClick={() =>
                            handleStatusUpdate(detailOrder._id, "refunded")
                          }
                          style={{
                            flex: 1,
                            padding: "10px",
                            borderRadius: 10,
                            border: "1.5px solid #ddd6fe",
                            background: "#fff",
                            color: "#7c3aed",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                            opacity: updatingId === detailOrder._id ? 0.6 : 1,
                          }}
                        >
                          ↩ Mark as Refunded
                        </button>
                      )}
                      {detailOrder.paymentStatus === "failed" && (
                        <button
                          disabled={updatingId === detailOrder._id}
                          onClick={() =>
                            handleStatusUpdate(detailOrder._id, "pending")
                          }
                          style={{
                            flex: 1,
                            padding: "10px",
                            borderRadius: 10,
                            border: "1.5px solid #fde68a",
                            background: "#fff",
                            color: "#d97706",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                            opacity: updatingId === detailOrder._id ? 0.6 : 1,
                          }}
                        >
                          🔄 Move to Pending
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
